export interface ProjectConfig {
  projectName: string;
  hostName?: string;
  frontendUrl?: string;
  backendUri?: string;
  portBase?: number;
  nodeEnv?: string;
  enablePlayground?: boolean;
  deploymentName: DeploymentName;
  adminEmails?: string[];
  adminUsers?: AdminUser[];
}

// values must be lowercase
export enum DeploymentName {
  /**
   * production is the main environment. it's used for the public-facing website and the production backend
   */
  PRODUCTION = "production",
  /**
   * staging is usually close to the production environment. it's used for testing before a new version is deployed to production
   */
  STAGING = "staging",
  /**
   * sandbox is like a playground for testing. usually it's auto-deployed on every commit so expect it to be unstable
   */
  SANDBOX = "sandbox",
  /**
   * preview is more stable than sandbox and is meant to be used by the commissioner for testing. it's usually deployed manually
   */
  PREVIEW = "preview",
  /**
   * the local development environment.
   */
  DEV = "dev",
}

export type AdminUser = {
  email: string;
  givenName: string;
  familyName: string;
};

export enum Targets {
  BACKEND = "backend",
  FRONTEND = "frontend",
  DEV = "dev",
  DB = "db",
}

export interface CorsConfig {
  allowMethods?: string[];
  allowHeaders?: string[];
  allowOriginList?: string[];
}

export interface ProxyConfig {
  port: string;
  cors?: CorsConfig;
}

export interface ServiceConfig {
  build?: {
    dockerfile?: string;
  };
  name?: string;
  command?: string[];
  image?: string;
  ports?: string[];
  volumes?: string[];
  backend_dependency?: boolean;
  networks?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  labels?: Record<string, any>;
  entrypoint?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  depends_on?: Record<string, any>;
  configs?: {
    source: string;
    target: string;
    uid?: string;
    mode?: string;
  }[];
  expose?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  healthcheck?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  environment?: Record<string, any>;
  proxy?: Record<string, ProxyConfig>;
  restart?: string;
  env_file?: string[];
}

export interface DockerComposeObject {
  version?: string;
  services: Record<string, ServiceConfig>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  volumes: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  networks?: Record<string, any>;
  restart?: string;
}

// ============================================================================
// Branded Types for Bucket Names
// ============================================================================

/**
 * Branded type for semantic bucket names.
 * Semantic names are used in code (e.g., "useravatar", "uploads").
 */
export type BucketName = string & {
  readonly __brand: "BucketName";
};

/**
 * Branded type for actual bucket names.
 * Actual names are the real Minio/S3 bucket names (e.g., "ap51-dev-uploads").
 */
export type BucketDeployment = string & {
  readonly __brand: "BucketDeployment";
};

/**
 * Creates a SemanticBucketName from a plain string.
 * Use this when defining bucket requirements in modules.
 */
export function bucketName(name: string): BucketName {
  return name as BucketName;
}

/** Semantic bucket name for user avatar storage (browser- and server-safe). */
export const USER_AVATAR_BUCKET: BucketName = bucketName("user-avatar");

/**
 * Creates an ActualBucketName from a plain string.
 * Use this when defining bucket mappings in deployment configs.
 */
export function deployedBucket(name: string): BucketDeployment {
  return name as BucketDeployment;
}

// ============================================================================
// Bucket Configuration Types
// ============================================================================

/**
 * Bucket requirement declared by a module.
 * Modules use this to specify which buckets they need at runtime.
 */
export interface RequiredBucket {
  /** Semantic name used in code (e.g., "useravatar", "uploads") */
  bucketName: BucketName;
  /** Whether the bucket needs public read access */
  public: boolean;
}

export interface S3CorsRule {
  allowedOrigins: string[];
  allowedMethods: string[];
  allowedHeaders?: string[];
  exposeHeaders?: string[];
  maxAgeSeconds?: number;
}

/**
 * Bucket mapping in deployment config.
 * Maps semantic bucket names to actual Minio/S3 bucket names.
 */
export interface S3BucketMapping {
  /** Semantic name - must match a RequiredBucket.semanticName */
  bucketName: BucketName;
  /** Actual bucket name in Minio/S3 */
  bucketDeployment: BucketDeployment;
  /** Public read access */
  public: boolean;
  /**
   * Bucket-level CORS rules applied by the S3 service on startup when CORS management is enabled.
   * Omit to leave existing rules unchanged; use `[]` to clear rules on external storage.
   */
  corsRules?: S3CorsRule[];
}

export type DockerComposeTransformer = (
  dockerComposeObj: DockerComposeObject,
  projectConfig: ProjectConfig,
) => void;

export interface ModuleConfig {
  prefix?: string;
  targets: TargetConfigs;
  additionalFiles?: {
    path: string;
    content: string | object;
  }[];
  services?: Record<string, ServiceConfig>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  volumes?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  networks?: Record<string, any>;
  configs?: Record<
    string,
    {
      content?: string;
      file?: string;
      external?: boolean;
      name?: string;
    }
  >;
  /**
   * Bucket requirements declared by this module.
   * The deployment compiler validates that all required buckets
   * are defined in the minio config of each deployment.
   */
  requiredBuckets?: RequiredBucket[];
}
export type ModuleConfigGenerator = (config: ProjectConfig) => ModuleConfig;
export type TargetConfigs = Partial<
  Record<Targets | string, Record<string, string>>
>;

export type WithRequiredKeys<T, K extends keyof T> = Partial<Omit<T, K>> &
  Required<Pick<T, K>>;

export type AutocompleteModuleConfig<
  ModuleOptionsType,
  RequiredKeys extends keyof ModuleOptionsType,
> = (
  projectSpecificModuleOptions: WithRequiredKeys<
    ModuleOptionsType,
    RequiredKeys
  >,
) => ModuleConfigGenerator;

/**
 * Describes WHEN an environment's CI deploy job runs. The deployment compiler
 * turns this into the GitLab `rules:` of the generated `deploy` / `track-deploy`
 * jobs (see tools/deployment-compiler/gitlab-deploy-generator.ts).
 */
export interface DeployTrigger {
  /**
   * "manual" → the job must be triggered by hand in the GitLab UI (non-blocking,
   * `allow_failure: true`); "auto" → it runs automatically when the conditions
   * (`branch` / `onTag`) match.
   *
   * A manual trigger with no `branch`/`onTag` is restricted to protected refs
   * (CI_COMMIT_REF_PROTECTED) rather than truly "any ref", so the deploy's
   * protected CI vars (SOPS_AGE_KEY / deploy-target token) aren't exposed to
   * arbitrary branch pipelines. It will therefore not appear on unprotected
   * feature branches.
   */
  mode: "manual" | "auto";
  /** Restrict to a single branch (e.g. "main", "next"). Omit for any branch. */
  branch?: string;
  /** Only run on release tags (matched against the compiler's release-tag regex). */
  onTag?: boolean;
}

/**
 * DNS handling for a Scaleway deployment. Either Scaleway hosts a delegated
 * subzone and OpenTofu creates the records ("managed"), or DNS lives elsewhere
 * and OpenTofu creates no records — the required records/targets are emitted via
 * tofu outputs for the operator to configure ("external").
 */
export type ScalewayDnsConfig =
  | {
      mode: "managed";
      /**
       * The delegated DNS zone OpenTofu manages records in (NS-delegated to
       * Scaleway), e.g. "emerge.example.com".
       */
      zone: string;
    }
  | {
      mode: "external";
      /**
       * Attach the custom domains (frontend on the Edge Services pipeline, api on
       * the container) and provision managed Let's Encrypt TLS. Leave false/unset
       * on the first apply: read the targets from the `frontend_edge_endpoint` /
       * `dns_records` outputs, create those CNAMEs in your DNS, then set this true
       * and re-apply. The managed certs can only validate once the records resolve,
       * so attachment is gated behind this flag for external DNS. (Managed mode
       * attaches immediately and ignores this.)
       */
      attachCustomDomains?: boolean;
    };

/**
 * Provisioning inputs for the Scaleway (OpenTofu) deploy target. The deployment
 * compiler turns these — together with the module configs — into the per-env
 * `deployment/<env>/opentofu/{main.tf.json,stack.auto.tfvars.json}` that reference
 * the shared `scaleway-stack` module.
 */
export interface ScalewayDeployTarget {
  type: "scaleway";
  /** Scaleway region, e.g. "fr-par". */
  region: string;
  /** Scaleway project id (plaintext identifier, like the notification module). */
  projectId: string;
  /** See {@link ScalewayDnsConfig}. */
  dns: ScalewayDnsConfig;
  /** Managed PostgreSQL (RDB) sizing. */
  database: {
    nodeType: string;
    isHaCluster?: boolean;
    volumeSizeGb?: number;
    /** Block-storage class, e.g. "sbs_5k" (default) or "sbs_15k". */
    volumeType?: string;
  };
  /** Managed Redis sizing. */
  redis: { nodeType: string; clusterSize?: number };
  /**
   * Serverless-container sizing. Scaling is pinned to a single always-on
   * instance (min_scale = max_scale = 1) in the module, so entrypoint DB
   * migrations run exactly once per deploy without concurrency.
   */
  container: { cpuLimitMvcpu: number; memoryLimitMb: number };
}

/**
 * The deployment backend an environment is shipped to. Discriminated by `type`
 * so additional targets can be added without touching the rule/wiring logic.
 */
export type DeployTargetConfig = { type: "coolify" } | ScalewayDeployTarget;

export type DeployTargetType = DeployTargetConfig["type"];

/**
 * Per-environment deployment configuration. Drives generation of the CI deploy
 * jobs. Omit it (e.g. for `dev`) to generate no deploy job at all.
 */
export interface DeploymentDeployConfig {
  target: DeployTargetConfig;
  trigger: DeployTrigger;
}

export type InstanceConfig<
  ProjectConfigType extends ProjectConfig = ProjectConfig,
> = {
  project: ProjectConfigType;
  projectSpecific?: TargetConfigs;
  modules: ModuleConfigGenerator[];
  transformers?: DockerComposeTransformer[];
  /**
   * How/when this environment is deployed in CI. When set, the deployment
   * compiler generates `deployment/<env>/gitlab-ci.generated.fragment.yml`. Omit to
   * skip deploy-job generation (e.g. local `dev`).
   */
  deploy?: DeploymentDeployConfig;
};

export function generateDefaultConfig(): ModuleConfigGenerator {
  return (config: ProjectConfig) => {
    const nodeEnv = config.nodeEnv ?? "development";
    const portBase = config.portBase;
    const frontendPort = portBase ? `${portBase}80` : "80";
    const backendPort = portBase ? `${portBase}30` : "3000";
    const backendUri = config.backendUri ?? `http://127.0.0.1:${backendPort}`;
    const frontendUri =
      config.frontendUrl ?? `http://127.0.0.1:${frontendPort}`;

    const projectName = config.projectName;
    const deploymentName = config.deploymentName;

    const moduleConfig: ModuleConfig = {
      targets: {
        [Targets.BACKEND]: {
          nodeEnv,
          backendPort,
          adminEmails: config.adminEmails?.join(",") ?? "",
          adminUsers: JSON.stringify(config.adminUsers),
          deploymentName,
          frontendUri,
          projectName,
        },
        [Targets.FRONTEND]: {
          frontendPort,
          backendUri,
          deploymentName,
          projectName,
        },
        [Targets.DEV]: {
          backendPort,
          frontendPort,
          deploymentName,
          backendUri,
          nodeEnv,
          projectName,
        },
      },
    };

    return moduleConfig;
  };
}
